import api from './api';
import { AxiosResponse } from 'axios';

interface ProfileData {
  name?: string;
  max_review_days?: number;
}

// score/notes/memorized_at are optional: when omitted in bulk saves the
// backend keeps the existing values for already-memorized pages.
interface PageData {
  page_number: number;
  memorized_at?: string;
  reviewed_at?: string;
  score?: number;
  notes?: string;
}

export const profileService = {
  getAll: (): Promise<AxiosResponse> => api.get('/profiles/list.php'),
  get: (id: number): Promise<AxiosResponse> => api.get(`/profiles/get.php?id=${id}`),
  create: (data: ProfileData): Promise<AxiosResponse> => api.post('/profiles/create.php', data),
  update: (id: number, data: ProfileData): Promise<AxiosResponse> =>
    api.put('/profiles/update.php', { id, ...data }),
  delete: (id: number): Promise<AxiosResponse> => api.delete(`/profiles/delete.php?id=${id}`),
  share: (profileId: number, email: string, permission: 'read' | 'edit'): Promise<AxiosResponse> =>
    api.post('/profiles/share.php', { profile_id: profileId, email, permission }),
  removeShare: (profileId: number, userId: number): Promise<AxiosResponse> =>
    api.delete(`/profiles/share.php?profile_id=${profileId}&user_id=${userId}`)
};

export const pageService = {
  getAll: (profileId: number): Promise<AxiosResponse> =>
    api.get(`/pages/list.php?profile_id=${profileId}`),
  save: (profileId: number, data: PageData): Promise<AxiosResponse> =>
    api.post('/pages/save.php', { profile_id: profileId, ...data }),
  bulkSave: (profileId: number, pages: PageData[]): Promise<AxiosResponse> =>
    api.post('/pages/bulk-save.php', { profile_id: profileId, pages }),
  delete: (profileId: number, pageNumber: number): Promise<AxiosResponse> =>
    api.delete(`/pages/delete.php?profile_id=${profileId}&page=${pageNumber}`),
  bulkDelete: (profileId: number, pageNumbers: number[]): Promise<AxiosResponse> =>
    api.post('/pages/bulk-delete.php', { profile_id: profileId, page_numbers: pageNumbers }),
  history: (profileId: number, pageNumber: number): Promise<AxiosResponse> =>
    api.get(`/pages/history.php?profile_id=${profileId}&page=${pageNumber}`),
  addReview: (profileId: number, pageNumber: number, score: number, notes: string): Promise<AxiosResponse> =>
    api.post('/pages/review.php', { profile_id: profileId, page: pageNumber, score, notes })
};

export const userService = {
  updateSettings: (data: { name?: string; email?: string }): Promise<AxiosResponse> =>
    api.put('/users/settings.php', data),
  changePassword: (oldPassword: string, newPassword: string): Promise<AxiosResponse> =>
    api.post('/users/password.php', { old_password: oldPassword, new_password: newPassword }),
  deleteAccount: (password: string): Promise<AxiosResponse> =>
    api.post('/users/delete.php', { password })
};
